import React, { useState } from "react";
import { format, parseISO } from "date-fns";
import { PriceIndexData, PriceDataPoint } from "@/types/price-index";
import { Pagination } from "@/components/bnp-ui";
import { ChevronUp, ChevronDown } from "@/components/bnp-icons";

interface DataTableProps {
  data: PriceIndexData[];
  isLoading?: boolean;
  pageSize?: number;
}

type SortKey =
  | "location"
  | "propertyType"
  | "averagePrice"
  | "medianPrice"
  | "totalProperties"
  | "lastUpdated";
type SortOrder = "asc" | "desc";

const DataTable: React.FC<DataTableProps> = ({
  data,
  isLoading = false,
  pageSize = 10,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortKey, setSortKey] = useState<SortKey>("lastUpdated");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortOrder("desc");
    }
  };

  const sortedData = React.useMemo(() => {
    if (!data || data.length === 0) return [];

    return [...data].sort((a, b) => {
      let aValue: any = a[sortKey];
      let bValue: any = b[sortKey];

      if (sortKey === "lastUpdated") {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortKey, sortOrder]);

  const paginatedData = React.useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, pageSize]);

  const SortIcon: React.FC<{ columnKey: SortKey }> = ({ columnKey }) => {
    if (sortKey !== columnKey) {
      return (
        <span className="text-grey-5 ml-1">
          <div className="w-3 h-3 inline-block -mb-1 [&>svg]:w-full [&>svg]:h-full">
            <ChevronUp />
          </div>
          <div className="w-3 h-3 inline-block -mt-1 [&>svg]:w-full [&>svg]:h-full">
            <ChevronDown />
          </div>
        </span>
      );
    }
    return sortOrder === "asc" ? (
      <div className="w-4 h-4 inline-block ml-1 text-bnp-cyan [&>svg]:w-full [&>svg]:h-full">
        <ChevronUp />
      </div>
    ) : (
      <div className="w-4 h-4 inline-block ml-1 text-bnp-cyan [&>svg]:w-full [&>svg]:h-full">
        <ChevronDown />
      </div>
    );
  };

  const TableHeader: React.FC<{
    label: string;
    sortKey: SortKey;
    className?: string;
  }> = ({ label, sortKey: key, className = "" }) => (
    <th
      className={`px-4 py-3 text-left text-xs font-semibold text-grey-4 uppercase tracking-wider cursor-pointer hover:bg-grey-6 transition-colors ${className}`}
      onClick={() => handleSort(key)}
    >
      <div className="flex items-center">
        {label}
        <SortIcon columnKey={key} />
      </div>
    </th>
  );

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-grey-3 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-grey-3">
            <thead className="bg-weak-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-grey-4 uppercase">
                  Location
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-grey-4 uppercase">
                  Property Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-grey-4 uppercase">
                  Avg Price
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-grey-4 uppercase">
                  Median Price
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-grey-4 uppercase">
                  Properties
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-grey-4 uppercase">
                  Last Updated
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-grey-3">
              {[...Array(5)].map((_, i) => (
                <tr key={i} className="animate-pulse">
                  <td className="px-4 py-4">
                    <div className="h-4 bg-grey-6 rounded w-24"></div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="h-4 bg-grey-6 rounded w-20"></div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="h-4 bg-grey-6 rounded w-28"></div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="h-4 bg-grey-6 rounded w-28"></div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="h-4 bg-grey-6 rounded w-16"></div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="h-4 bg-grey-6 rounded w-24"></div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-grey-3 p-8">
        <div className="text-center">
          <p className="text-grey-4 text-sm">No data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-grey-3 overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-grey-3">
          <thead className="bg-weak-100">
            <tr>
              <TableHeader label="Location" sortKey="location" />
              <TableHeader label="Property Type" sortKey="propertyType" />
              <TableHeader label="Avg Price" sortKey="averagePrice" />
              <TableHeader label="Median Price" sortKey="medianPrice" />
              <TableHeader label="Properties" sortKey="totalProperties" />
              <TableHeader label="Last Updated" sortKey="lastUpdated" />
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-grey-3">
            {paginatedData.map((item) => (
              <tr key={item.id} className="hover:bg-weak-100 transition-colors">
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className="text-sm font-medium text-main-900">
                    {item.location}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className="text-sm text-grey-4 capitalize">
                    {item.propertyType}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className="text-sm font-semibold text-bnp-cyan">
                    ₦{item.averagePrice.toLocaleString()}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className="text-sm text-grey-4">
                    ₦{item.medianPrice.toLocaleString()}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className="text-sm text-grey-4">
                    {item.totalProperties.toLocaleString()}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className="text-sm text-grey-4">
                    {format(parseISO(item.lastUpdated), "MMM dd, yyyy")}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden divide-y divide-grey-3">
        {paginatedData.map((item) => (
          <div
            key={item.id}
            className="p-4 hover:bg-weak-100 transition-colors"
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="text-sm font-semibold text-main-900">
                  {item.location}
                </h4>
                <p className="text-xs text-grey-4 capitalize mt-1">
                  {item.propertyType}
                </p>
              </div>
              <span className="text-xs text-grey-4">
                {format(parseISO(item.lastUpdated), "MMM dd")}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-grey-4 mb-1">Avg Price</p>
                <p className="text-sm font-semibold text-bnp-cyan">
                  ₦{item.averagePrice.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs text-grey-4 mb-1">Median Price</p>
                <p className="text-sm text-main-900">
                  ₦{item.medianPrice.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs text-grey-4 mb-1">Properties</p>
                <p className="text-sm text-main-900">
                  {item.totalProperties.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {data.length > pageSize && (
        <div className="px-4 py-3 bg-weak-100 border-t border-grey-3">
          <Pagination
            metadata={{
              totalCount: data.length,
              pageSize: pageSize,
            }}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
};

export default DataTable;
